import _asyncToGenerator from "/vercel/path0/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "/vercel/path0/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/regenerator";
import { filterBuffer } from '../helpers/filter-buffer';
import { isOwnedByContext } from '../helpers/is-owned-by-context';

var filterFullBuffer = function filterFullBuffer(renderedBuffer, nativeOfflineAudioContext, feedback, feedforward) {
  var convertedFeedback = feedback instanceof Float64Array ? feedback : new Float64Array(feedback);
  var convertedFeedforward = feedforward instanceof Float64Array ? feedforward : new Float64Array(feedforward);
  var feedbackLength = convertedFeedback.length;
  var feedforwardLength = convertedFeedforward.length;
  var minLength = Math.min(feedbackLength, feedforwardLength);

  if (convertedFeedback[0] !== 1) {
    for (var i = 0; i < feedbackLength; i += 1) {
      convertedFeedforward[i] /= convertedFeedback[0];
    }

    for (var _i = 1; _i < feedforwardLength; _i += 1) {
      convertedFeedback[_i] /= convertedFeedback[0];
    }
  }

  var bufferLength = 32;
  var xBuffer = new Float32Array(bufferLength);
  var yBuffer = new Float32Array(bufferLength);
  var filteredBuffer = nativeOfflineAudioContext.createBuffer(renderedBuffer.numberOfChannels, renderedBuffer.length, renderedBuffer.sampleRate);
  var numberOfChannels = renderedBuffer.numberOfChannels;

  for (var _i2 = 0; _i2 < numberOfChannels; _i2 += 1) {
    var input = renderedBuffer.getChannelData(_i2);
    var output = filteredBuffer.getChannelData(_i2);
    xBuffer.fill(0);
    yBuffer.fill(0);
    filterBuffer(convertedFeedback, feedbackLength, convertedFeedforward, feedforwardLength, minLength, xBuffer, yBuffer, 0, bufferLength, input, output);
  }

  return filteredBuffer;
};

export var createIIRFilterNodeRendererFactory = function createIIRFilterNodeRendererFactory(createNativeAudioBufferSourceNode, getNativeAudioNode, nativeOfflineAudioContextConstructor, renderInputsOfAudioNode, renderNativeOfflineAudioContext) {
  return function (feedback, feedforward) {
    var renderedNativeAudioNodes = new WeakMap();
    var filteredBufferPromise = null;

    var createAudioNode = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(proxy, nativeOfflineAudioContext, trace) {
        var nativeAudioBufferSourceNode, nativeIIRFilterNode, nativeIIRFilterNodeIsOwnedByContext, partialOfflineAudioContext, filteredBuffer;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                nativeAudioBufferSourceNode = null;
                nativeIIRFilterNode = getNativeAudioNode(proxy); // If the initially used nativeIIRFilterNode was not constructed on the same OfflineAudioContext it needs to be created again.

                nativeIIRFilterNodeIsOwnedByContext = isOwnedByContext(nativeIIRFilterNode, nativeOfflineAudioContext); // Bug #9: Safari does not support IIRFilterNodes.

                if (nativeOfflineAudioContext.createIIRFilter === undefined) {
                  nativeAudioBufferSourceNode = createNativeAudioBufferSourceNode(nativeOfflineAudioContext, {
                    buffer: null,
                    channelCount: 2,
                    channelCountMode: 'max',
                    channelInterpretation: 'speakers',
                    loop: false,
                    loopEnd: 0,
                    loopStart: 0,
                    playbackRate: 1
                  });
                } else if (!nativeIIRFilterNodeIsOwnedByContext) {
                  // @todo TypeScript defines the parameters of createIIRFilter() as arrays of numbers.
                  nativeIIRFilterNode = nativeOfflineAudioContext.createIIRFilter(feedforward, feedback);
                }

                renderedNativeAudioNodes.set(nativeOfflineAudioContext, nativeAudioBufferSourceNode === null ? nativeIIRFilterNode : nativeAudioBufferSourceNode);

                if (!(nativeAudioBufferSourceNode !== null)) {
                  _context2.next = 17;
                  break;
                }

                if (!(filteredBufferPromise === null)) {
                  _context2.next = 11;
                  break;
                }

                if (!(nativeOfflineAudioContextConstructor === null)) {
                  _context2.next = 9;
                  break;
                }

                throw new Error('Missing the native OfflineAudioContext constructor.');

              case 9:
                partialOfflineAudioContext = new nativeOfflineAudioContextConstructor( // Bug #47: The AudioDestinationNode in Safari gets not initialized correctly.
                proxy.context.destination.channelCount, // Bug #17: Safari does not yet expose the length.
                proxy.context.length, nativeOfflineAudioContext.sampleRate);
                filteredBufferPromise = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
                  var renderedBuffer;
                  return _regeneratorRuntime.wrap(function _callee$(_context) {
                    while (1) {
                      switch (_context.prev = _context.next) {
                        case 0:
                          _context.next = 2;
                          return renderInputsOfAudioNode(proxy, partialOfflineAudioContext, partialOfflineAudioContext.destination, trace);

                        case 2:
                          _context.next = 4;
                          return renderNativeOfflineAudioContext(partialOfflineAudioContext);

                        case 4:
                          renderedBuffer = _context.sent;
                          return _context.abrupt("return", filterFullBuffer(renderedBuffer, nativeOfflineAudioContext, feedback, feedforward));

                        case 6:
                        case "end":
                          return _context.stop();
                      }
                    }
                  }, _callee);
                }))();

              case 11:
                _context2.next = 13;
                return filteredBufferPromise;

              case 13:
                filteredBuffer = _context2.sent;
                nativeAudioBufferSourceNode.buffer = filteredBuffer;
                nativeAudioBufferSourceNode.start(0);
                return _context2.abrupt("return", nativeAudioBufferSourceNode);

              case 17:
                _context2.next = 19;
                return renderInputsOfAudioNode(proxy, nativeOfflineAudioContext, nativeIIRFilterNode, trace);

              case 19:
                return _context2.abrupt("return", nativeIIRFilterNode);

              case 20:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }));

      return function createAudioNode(_x, _x2, _x3) {
        return _ref.apply(this, arguments);
      };
    }();

    return {
      render: function render(proxy, nativeOfflineAudioContext, trace) {
        var renderedNativeAudioNode = renderedNativeAudioNodes.get(nativeOfflineAudioContext);

        if (renderedNativeAudioNode !== undefined) {
          return Promise.resolve(renderedNativeAudioNode);
        }

        return createAudioNode(proxy, nativeOfflineAudioContext, trace);
      }
    };
  };
};